import { createTypedSelector } from '@newStore/genericHelpers';
import { escape, get, orderBy } from 'lodash';

import type {
  Content,
  ContentHref,
  WebPage,
  WebTemplate,
  WebTemplateHref,
} from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectContentItem,
  selectPathToRootWebConfigurations,
  selectRawWebPage,
  selectWebPage,
} from '@newStore/documentApi/documentApiSelectors';
import { selectWebsiteHrefForRoot } from '@newStore/documentUI/nodeTypeConfigSelectors';
import { stringDiffArrowFunction } from '@newStore/documentUI/transformProposal/asideDiffText';
import { selectWebTemplates } from '@newStore/externalData/externalDataSelectors';
import { pathMap } from '@newStore/externalData/externalDataTypes';
import type { AsideChangeMessageSelector, EditAsideWebType } from '@nodeTypeConfig/configTypes';
import { selectCurrentWebConfig } from '@store/selectors/asideSelectors';

export const selectIsWebTypeComponentLoading = (state: RootState) =>
  state.externalData.data[pathMap.webTemplates]?.isLoading;

export const isContentTypeValidForWebTemplate = (
  template: WebTemplate,
  content: Content,
): boolean => {
  return (
    !template.allowedContent ||
    template.allowedContent.some((ac) => {
      return (
        (!ac.type || ac.type === content.type) && // no type specified OR matches if specified
        (!ac.tag || content.tags?.includes(ac.tag)) // no tag specified OR matches if specified
      );
    })
  );
};

export const isTemplateForChildValid = (
  template: WebTemplate,
  parentTemplate: WebTemplate | null,
): boolean => {
  if (!parentTemplate || !parentTemplate.allowedTemplatesForChildNode) {
    return true;
  }

  if (parentTemplate.allowedTemplatesForChildNode.length === 0) {
    return false;
  }

  return parentTemplate.allowedTemplatesForChildNode.includes(template.$$meta.permalink);
};

const templateToOption = (template: WebTemplate) => ({
  key: template.key,
  href: template.$$meta.permalink,
  display: `${template.name}`,
});

const webTemplatesToOmit: WebTemplateHref[] = [
  '/web/templates/3d50fcaa-ea2c-48bd-a424-1d371d1ec269' as WebTemplateHref, // CURRICULA_NAVIGATION
  '/web/templates/980e5264-e611-4ced-bf41-181ef7c81f2c' as WebTemplateHref, // HOME_PAGE
  '/web/templates/0d4092bf-584b-461c-a723-b031b3633e18' as WebTemplateHref, // GLOBAL_TRAININGSDATABASE
];

const selectAllWebTypeOptions = createTypedSelector(
  [(state) => selectWebTemplates(state)],
  (templatesRecord) => Object.values(templatesRecord).map(templateToOption),
);

export const selectWebTypeOptions = createTypedSelector(
  [
    (state) => selectWebTemplates(state),
    (state, href: ContentHref) => selectContentItem(state, href),
    (state, href: ContentHref) => selectWebsiteHrefForRoot(state, href),
    (state, href: ContentHref) => selectPathToRootWebConfigurations(state, href),
  ],
  (templatesRecord, content, websiteHref, pathToRootWebConfigs) => {
    if (!websiteHref) {
      return [];
    }
    const templates = Object.values(templatesRecord);
    const parentWebConfig: WebPage | undefined = pathToRootWebConfigs[1];
    const parentWebTemplate: WebTemplate | null =
      (parentWebConfig && templatesRecord[parentWebConfig.template.href]) || null;

    const possibleTemplates = templates.filter((tp) => {
      const sameWebsite = tp.website.href === websiteHref;

      const contentMatched = isContentTypeValidForWebTemplate(tp, content);

      const templateForChildMatched = isTemplateForChildValid(tp, parentWebTemplate);

      const notOmitted = !webTemplatesToOmit.includes(tp.$$meta.permalink);

      const matched = sameWebsite && contentMatched && templateForChildMatched && notOmitted;
      return matched;
    });

    return orderBy(possibleTemplates, 'name').map(templateToOption);
  },
);

export const selectSelectedType = createTypedSelector(
  [
    (state) => selectCurrentWebConfig(state),
    selectAllWebTypeOptions,
    (_state, config: EditAsideWebType) => config,
  ],
  (currentNode, options, config) =>
    options.find((o) => o.href === get(currentNode, config.property)),
);

export const selectChangeMessageForWebType: AsideChangeMessageSelector<EditAsideWebType> =
  createTypedSelector(
    [
      (state, href: ContentHref) => selectWebPage(state, href),
      (state, href: ContentHref) => selectRawWebPage(state, href),
      (state) => selectAllWebTypeOptions(state),
      (state, href: ContentHref, config: EditAsideWebType) => config,
    ],
    (webPage, originalWebPage, options, config): string | null => {
      if (!config.property) {
        console.error('No property found for config', config);
        throw Error(`Can not convert ${config.property} to a string`);
      }

      const newHref = get(webPage, config.property);
      const oldHref = get(originalWebPage, config.property);

      const newValue = escape(options.find((o) => o.href === newHref)?.display ?? '');
      const oldValue = escape(options.find((o) => o.href === oldHref)?.display ?? '');

      const diffText = stringDiffArrowFunction(newValue, oldValue);

      return diffText;
    },
  );
